import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Gavel, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import {
  Card,
  CardContent,
} from "@/components/ui/Card";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CourtAdvocacyRecord {
  id: number;
  person_id: number;
  agency_id: number;
  court_id: number | null;
  judge_id: number | null;
  hearing_date: string | null;
  hearing_type: string | null;
  disposition: string | null;
  charges: string | null;
  notes: string | null;
  staff_id: number | null;
  created_at: string;
  court_name?: string;
  judge_name?: string;
  staff_first_name?: string;
  staff_last_name?: string;
}

interface CourtOption {
  id: number;
  name: string;
}

interface JudgeOption {
  id: number;
  name: string;
}

const hearingTypeOptions = [
  { value: "arraignment", label: "Arraignment" },
  { value: "preliminary_hearing", label: "Preliminary Hearing" },
  { value: "trial", label: "Trial" },
  { value: "sentencing", label: "Sentencing" },
  { value: "protection_order", label: "Protection Order Hearing" },
  { value: "custody", label: "Custody Hearing" },
  { value: "divorce", label: "Divorce Hearing" },
  { value: "contempt", label: "Contempt Hearing" },
  { value: "other", label: "Other" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CourtAdvocacyListProps {
  personId: string;
}

export function CourtAdvocacyList({ personId }: CourtAdvocacyListProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState<number | null>(null);

  const { data, isLoading } = useQuery<{ data: CourtAdvocacyRecord[] }>({
    queryKey: ["courtAdvocacy", personId],
    queryFn: () =>
      api.get<{ data: CourtAdvocacyRecord[] }>(
        `/clients/${personId}/court-advocacy`,
      ),
    enabled: !!personId,
  });

  const { data: courtsData } = useQuery<{ data: CourtOption[] }>({
    queryKey: ["courts"],
    queryFn: () => api.get<{ data: CourtOption[] }>("/legal/courts"),
  });

  const { data: judgesData } = useQuery<{ data: JudgeOption[] }>({
    queryKey: ["judges"],
    queryFn: () => api.get<{ data: JudgeOption[] }>("/legal/judges"),
  });

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post(`/clients/${personId}/court-advocacy`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["courtAdvocacy", personId],
      });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...body }: { id: number } & Record<string, unknown>) =>
      api.put(`/clients/${personId}/court-advocacy/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["courtAdvocacy", personId],
      });
      setEditingRecordId(null);
    },
  });

  const records = data?.data ?? [];
  const courts = courtsData?.data ?? [];
  const judges = judgesData?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Court Advocacy</h3>
        <Button size="sm" className="gap-2" onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4" />
          Add Record
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="p-4">
            <CourtAdvocacyForm
              courts={courts}
              judges={judges}
              onSubmit={(d) => createMutation.mutate(d)}
              onCancel={() => setShowForm(false)}
              isLoading={createMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="h-32 animate-pulse rounded-lg border bg-muted" />
      )}

      {!isLoading && records.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Gavel className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No court advocacy records found.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && records.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Court
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Judge
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Disposition
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Staff
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => {
                  const staffName =
                    r.staff_first_name && r.staff_last_name
                      ? `${r.staff_first_name} ${r.staff_last_name}`
                      : "-";
                  const isEditing = editingRecordId === r.id;

                  if (isEditing) {
                    return (
                      <tr key={r.id} className="border-b last:border-0">
                        <td colSpan={7} className="px-4 py-4">
                          <p className="text-sm font-medium mb-3">Edit Court Advocacy Record</p>
                          <CourtAdvocacyForm
                            initial={r}
                            courts={courts}
                            judges={judges}
                            onSubmit={(d) =>
                              updateMutation.mutate({ id: r.id, ...d })
                            }
                            onCancel={() => setEditingRecordId(null)}
                            isLoading={updateMutation.isPending}
                          />
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="px-4 py-3">
                        {r.hearing_date
                          ? new Date(r.hearing_date).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="px-4 py-3">
                        {r.hearing_type ? (
                          <span className="inline-flex rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground capitalize">
                            {r.hearing_type.replace(/_/g, " ")}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-4 py-3">{r.court_name ?? "-"}</td>
                      <td className="px-4 py-3">{r.judge_name ?? "-"}</td>
                      <td className="px-4 py-3">{r.disposition ?? "-"}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {staffName}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingRecordId(r.id);
                            setShowForm(false);
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

interface CourtAdvocacyFormProps {
  initial?: CourtAdvocacyRecord;
  courts: CourtOption[];
  judges: JudgeOption[];
  onSubmit: (data: Record<string, unknown>) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function CourtAdvocacyForm({
  initial,
  courts,
  judges,
  onSubmit,
  onCancel,
  isLoading,
}: CourtAdvocacyFormProps) {
  const [hearingDate, setHearingDate] = useState(
    initial?.hearing_date ?? "",
  );
  const [hearingType, setHearingType] = useState(
    initial?.hearing_type ?? "",
  );
  const [courtId, setCourtId] = useState(
    initial?.court_id ? String(initial.court_id) : "",
  );
  const [judgeId, setJudgeId] = useState(
    initial?.judge_id ? String(initial.judge_id) : "",
  );
  const [disposition, setDisposition] = useState(initial?.disposition ?? "");
  const [charges, setCharges] = useState(initial?.charges ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");

  const courtOptions = courts.map((c) => ({
    value: String(c.id),
    label: c.name,
  }));
  const judgeOptions = judges.map((j) => ({
    value: String(j.id),
    label: j.name,
  }));

  function handleSubmit() {
    const data: Record<string, unknown> = {};
    if (hearingDate) data.hearingDate = hearingDate;
    if (hearingType) data.hearingType = hearingType;
    if (courtId) data.courtId = parseInt(courtId, 10);
    if (judgeId) data.judgeId = parseInt(judgeId, 10);
    if (disposition) data.disposition = disposition;
    if (charges) data.charges = charges;
    if (notes) data.notes = notes;
    onSubmit(data);
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Hearing Date"
          type="date"
          value={hearingDate}
          onChange={(e) => setHearingDate(e.target.value)}
        />
        <Select
          label="Hearing Type"
          options={hearingTypeOptions}
          value={hearingType}
          onChange={setHearingType}
          placeholder="Select type..."
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Select
          label="Court"
          options={courtOptions}
          value={courtId}
          onChange={setCourtId}
          placeholder="Select court..."
          searchable
        />
        <Select
          label="Judge"
          options={judgeOptions}
          value={judgeId}
          onChange={setJudgeId}
          placeholder="Select judge..."
          searchable
        />
      </div>
      <Input
        label="Charges"
        value={charges}
        onChange={(e) => setCharges(e.target.value)}
        placeholder="Charges description..."
      />
      <Input
        label="Disposition"
        value={disposition}
        onChange={(e) => setDisposition(e.target.value)}
        placeholder="Hearing outcome..."
      />
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Additional notes..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div className="flex gap-3">
        <Button size="sm" onClick={handleSubmit} loading={isLoading}>
          {initial ? "Update Record" : "Create Record"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
